import React, { useState, useEffect, useMemo } from 'react';
import { pocket } from '../api';
import RhetoricTagPicker from './RhetoricTagPicker';
import SkillCategoryPicker from './SkillCategoryPicker';
import { BRANCHES } from './SkillTree/data';
import './TeachingMomentsSection.css';

const UNGROUPED = 'Ungrouped';

// Same naming as the saved categories (backend utils/skillCategories.js): the path with the top "Technique" dropped
const nodeName = (path) => (path[0] === 'Technique' && path.length > 1 ? path.slice(1) : path).join(' › ');

const MomentCard = ({ moment, onTagged, onThemeChanged, onState, onAddToPlan }) => {
  const handleTag = async (tag) => {
    const res = await pocket.tagTeachingMomentRhetoric(moment.id, tag);
    onTagged(moment.id, res.data.rhetoricTags);
  };

  const handleThemeChange = async (themeName) => {
    const res = await pocket.setTeachingMomentTheme(moment.id, themeName);
    onThemeChanged(moment.id, res.data.themes);
  };

  return (
    <div className={`moment-card${moment.keep ? ' is-kept' : ''}${moment.hidden ? ' is-hidden' : ''}`}>
      <div className="moment-card-head">
        {moment.label ? <div className="moment-card-label">{moment.label}</div> : <span />}
        <div className="moment-card-quick">
          <button type="button" className={`moment-quick${moment.keep ? ' on' : ''}`} onClick={() => onState(moment.id, { keep: !moment.keep })} aria-pressed={moment.keep} aria-label={moment.keep ? 'Remove the star' : 'Star this teaching'} title={moment.keep ? 'Starred' : 'Star it'}>{moment.keep ? '⭐' : '☆'}</button>
          <button type="button" className="moment-quick" onClick={() => onState(moment.id, { hidden: !moment.hidden })} aria-label={moment.hidden ? 'Show this teaching again' : 'Hide this teaching'} title={moment.hidden ? 'Show again' : 'Hide it'}>{moment.hidden ? '👁️' : '🙈'}</button>
        </div>
      </div>
      <p className="moment-card-text">{moment.text}</p>
      <div className="moment-card-meta">
        <span>👤 {moment.playerName || 'Unknown'}{moment.isUnassigned && ' (unassigned)'}</span>
        <span>{moment.date ? new Date(moment.date).toLocaleDateString() : ''}</span>
      </div>
      <div className="moment-card-category">
        {moment.themes.length > 0 && (
          <span className="moment-card-theme">🏷️ {moment.themes.join(', ')}</span>
        )}
        <SkillCategoryPicker
          currentCategory={moment.themes[0] || null}
          onSelect={handleThemeChange}
          buttonLabel={moment.themes.length > 0 ? '✏️ Edit category' : '+ Set category'}
        />
      </div>
      <div className="moment-card-tags">
        {moment.rhetoricTags.map((tag, idx) => (
          <span key={idx} className="moment-rhetoric-tag">{tag}</span>
        ))}
        <RhetoricTagPicker existingTags={moment.rhetoricTags} onTag={handleTag} />
      </div>
      {onAddToPlan && (
        <button type="button" className="moment-add-plan" onClick={() => onAddToPlan(moment)}>🎬 Add to a video</button>
      )}
    </div>
  );
};

// ── the skill tree with a count on every node: where the teachings are, and where there are gaps ──
const SkillCountTree = ({ counts, uncategorised, selected, onSelect }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => setOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const total = (node, parentPath) => {
    const path = [...parentPath, node.title];
    const own = counts[nodeName(path)] || 0;
    return (node.children || []).reduce((sum, c) => sum + total(c, path), own);
  };

  const rows = (nodes, parentPath, depth) => nodes.map(node => {
    const path = [...parentPath, node.title];
    const name = nodeName(path);
    const n = total(node, parentPath);
    const kids = node.children && node.children.length > 0;
    const isOpen = open.has(node.id);
    return (
      <div key={node.id}>
        <div className={`stc-row${selected === name ? ' is-selected' : ''}${n === 0 ? ' is-empty' : ''}`} style={{ paddingLeft: 4 + depth * 14 }}>
          {kids ? (
            <button type="button" className={`stc-chev${isOpen ? ' is-open' : ''}`} onClick={() => toggle(node.id)} aria-label={isOpen ? `Collapse ${node.title}` : `Expand ${node.title}`} aria-expanded={isOpen}>▸</button>
          ) : <span className="stc-chev stc-dot" aria-hidden="true">•</span>}
          <button type="button" className="stc-name" onClick={() => onSelect(selected === name ? null : name)}>{node.title}</button>
          <span className={`stc-count${n === 0 ? ' zero' : ''}`}>{n}</span>
        </div>
        {kids && isOpen && rows(node.children, path, depth + 1)}
      </div>
    );
  });

  return (
    <div className="stc">
      {uncategorised > 0 && (
        <div className={`stc-row${selected === '__none' ? ' is-selected' : ''}`}>
          <span className="stc-chev stc-dot" aria-hidden="true">•</span>
          <button type="button" className="stc-name" onClick={() => onSelect(selected === '__none' ? null : '__none')}>Not categorised yet</button>
          <span className="stc-count">{uncategorised}</span>
        </div>
      )}
      {rows(BRANCHES, [], 0)}
    </div>
  );
};

// every category name inside a node's branch of the tree (the node itself and everything below it)
const namesUnder = (name) => {
  const out = new Set();
  const walk = (node, parentPath, inside) => {
    const path = [...parentPath, node.title];
    const here = nodeName(path);
    const nowInside = inside || here === name;
    if (nowInside) out.add(here);
    (node.children || []).forEach(c => walk(c, path, nowInside));
  };
  BRANCHES.forEach(b => walk(b, [], false));
  return out;
};

/**
 * The coach's library of "clear teachings" pulled from lesson summaries. Star (⭐) the good ones, hide the noise,
 * see how many teachings sit under every skill of the skill tree, tap a skill to focus on it, and send teachings
 * to a video plan. Rhetorical tags and categories are set by the coach (a category can only be a skill-tree node).
 */
const TeachingMomentsSection = ({ coachId, onAddToPlan }) => {
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'kept' | 'hidden'
  const [focus, setFocus] = useState(null);    // a skill name, '__none', or null
  const [showTree, setShowTree] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    pocket.getTeachingMoments(coachId)
      .then(res => {
        if (!cancelled) setMoments(res.data?.moments || []);
      })
      .catch(err => {
        console.error('❌ Error loading teaching moments:', err);
        if (!cancelled) setError('Failed to load teaching moments');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [coachId]);

  const handleTagged = (momentId, rhetoricTags) => {
    setMoments(prev => prev.map(m => m.id === momentId ? { ...m, rhetoricTags } : m));
  };

  const handleThemeChanged = (momentId, themes) => {
    setMoments(prev => prev.map(m => m.id === momentId ? { ...m, themes } : m));
  };

  const handleState = async (momentId, change) => {
    const before = moments;
    setMoments(prev => prev.map(m => {
      if (m.id !== momentId) return m;
      const next = { ...m, ...change };
      if (change.keep === true) next.hidden = false;
      if (change.hidden === true) next.keep = false;
      return next;
    }));
    try {
      await pocket.setTeachingMomentState(momentId, change);
    } catch (err) {
      console.error('❌ Could not save the change:', err);
      setMoments(before);
    }
  };

  const counts = useMemo(() => {
    const c = {};
    let none = 0;
    for (const m of moments) {
      if (filter === 'hidden' ? !m.hidden : m.hidden) continue; // the tree follows the current filter
      if (filter === 'kept' && !m.keep) continue;
      if (m.themes.length === 0) { none++; continue; }
      c[m.themes[0]] = (c[m.themes[0]] || 0) + 1;
    }
    return { byName: c, none };
  }, [moments, filter]);

  const keptCount = moments.filter(m => m.keep && !m.hidden).length;
  const hiddenCount = moments.filter(m => m.hidden).length;
  const activeCount = moments.length - hiddenCount;

  const visible = useMemo(() => {
    const allowed = focus && focus !== '__none' ? namesUnder(focus) : null;
    return moments.filter(m => {
      if (filter === 'hidden' ? !m.hidden : m.hidden) return false;
      if (filter === 'kept' && !m.keep) return false;
      if (focus === '__none') return m.themes.length === 0;
      if (allowed) return m.themes.length > 0 && allowed.has(m.themes[0]);
      return true;
    });
  }, [moments, filter, focus]);

  if (loading) {
    return <div className="teaching-moments-status">Loading teaching moments...</div>;
  }
  if (error) {
    return <div className="teaching-moments-status teaching-moments-status-error">❌ {error}</div>;
  }
  if (moments.length === 0) {
    return (
      <div className="teaching-moments-status">
        No teaching moments found yet. These are pulled automatically from Pocket lesson
        summaries formatted as a labeled point (e.g. "**Kinetic Chain:** Power must be
        channeled..."). Plain-prose summaries without that structure won't surface anything here.
      </div>
    );
  }

  // group the visible teachings by their skill category, biggest group first
  const bySkill = {};
  for (const moment of visible) {
    const key = moment.themes[0] || UNGROUPED;
    if (!bySkill[key]) bySkill[key] = [];
    bySkill[key].push(moment);
  }
  const groupNames = Object.keys(bySkill).sort((a, b) => {
    if (a === UNGROUPED) return 1;
    if (b === UNGROUPED) return -1;
    return bySkill[b].length - bySkill[a].length;
  });

  return (
    <div className="teaching-moments-section">
      <p className="teaching-moments-subtitle">
        {activeCount} teaching{activeCount !== 1 ? 's' : ''} from your lessons. Star the ones worth a video, then add them to a video plan.
      </p>

      <div className="teaching-moments-filter">
        <button type="button" className={`teaching-moments-filter-pill ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All ({activeCount})</button>
        <button type="button" className={`teaching-moments-filter-pill ${filter === 'kept' ? 'active' : ''}`} onClick={() => setFilter('kept')}>⭐ Starred ({keptCount})</button>
        {hiddenCount > 0 && (
          <button type="button" className={`teaching-moments-filter-pill ${filter === 'hidden' ? 'active' : ''}`} onClick={() => setFilter('hidden')}>🙈 Hidden ({hiddenCount})</button>
        )}
      </div>

      <button type="button" className="tm-tree-toggle" onClick={() => setShowTree(v => !v)} aria-expanded={showTree}>
        🎯 {focus ? (focus === '__none' ? 'Not categorised yet' : focus) : 'By skill'} <span className="tm-tree-hint">{showTree ? 'Hide skill tree' : 'Show skill tree'}</span>
      </button>
      {focus && (
        <button type="button" className="tm-clear-focus" onClick={() => setFocus(null)}>✕ Show all skills</button>
      )}
      {showTree && (
        <SkillCountTree counts={counts.byName} uncategorised={counts.none} selected={focus} onSelect={(n) => setFocus(n)} />
      )}

      {visible.length === 0 ? (
        <div className="teaching-moments-status">
          {filter === 'kept' ? 'Nothing starred here yet. Tap ☆ on a teaching to keep it.' : filter === 'hidden' ? 'Nothing hidden.' : 'No teachings under this skill yet.'}
        </div>
      ) : groupNames.map(group => (
        <div key={group} className="teaching-moments-theme-group">
          <h3 className="teaching-moments-theme-title">{group}</h3>
          <div className="teaching-moments-grid">
            {bySkill[group].map(moment => (
              <MomentCard
                key={moment.id}
                moment={moment}
                onTagged={handleTagged}
                onThemeChanged={handleThemeChanged}
                onState={handleState}
                onAddToPlan={onAddToPlan}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TeachingMomentsSection;
